import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";
import { cn } from "../../lib/cn";
import { RouterLink } from "./router-link";

type ButtonSize = "sm" | "md" | "lg";

const buttonStyles = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 rounded-full border leading-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: null,
        secondary: null,
        ghost: null,
      },
      tone: {
        neutral: null,
        danger: null,
        success: null,
      },
      selected: {
        true: null,
        false: null,
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        tone: "neutral",
        class:
          "border-[var(--brand)] bg-[var(--brand)] text-[var(--brand-contrast)] hover:border-[var(--brand-dark)] hover:bg-[var(--brand-dark)]",
      },
      {
        variant: "secondary",
        tone: "neutral",
        selected: false,
        class:
          "border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-text)] hover:border-[color-mix(in_srgb,var(--color-text)_18%,transparent)] hover:bg-[var(--color-surface-2)]",
      },
      {
        variant: "secondary",
        tone: "neutral",
        selected: true,
        class:
          "border-[var(--brand)] bg-[var(--brand-soft)] text-[var(--color-text)] hover:border-[var(--brand-dark)] hover:bg-[var(--brand-soft-2)]",
      },
      {
        variant: "ghost",
        tone: "neutral",
        class:
          "border-[var(--color-border-strong)] bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]",
      },
      {
        variant: "primary",
        tone: "danger",
        class:
          "border-[var(--color-danger-tint)] bg-[var(--color-danger)] text-[var(--color-surface)] hover:brightness-95",
      },
      {
        variant: "secondary",
        tone: "danger",
        class:
          "border-[color-mix(in_srgb,var(--color-danger-tint)_40%,transparent)] bg-[var(--color-surface)] text-[var(--color-danger)] hover:border-[var(--color-danger-tint)] hover:bg-[var(--color-danger-soft)]",
      },
      {
        variant: "ghost",
        tone: "danger",
        class:
          "border-transparent bg-transparent text-[var(--color-danger)] hover:border-[var(--color-danger-tint)] hover:bg-[var(--color-danger-soft)]",
      },
      {
        variant: "primary",
        tone: "success",
        class:
          "border-[var(--color-success)] bg-[var(--color-success)] text-[var(--color-surface)] hover:brightness-95",
      },
      {
        variant: "secondary",
        tone: "success",
        class:
          "border-[color-mix(in_srgb,var(--color-success-ring)_30%,transparent)] bg-[var(--color-success-soft)] text-[var(--color-success)] hover:border-[var(--color-success)] hover:bg-[color-mix(in_srgb,var(--color-success-ring)_20%,transparent)]",
      },
      {
        variant: "ghost",
        tone: "success",
        class:
          "border-transparent bg-transparent text-[var(--color-success)] hover:border-[var(--color-success-ring)] hover:bg-[var(--color-success-soft)]",
      },
    ],
    defaultVariants: {
      variant: "secondary",
      tone: "neutral",
      selected: false,
    },
  },
);

type ButtonStyleProps = VariantProps<typeof buttonStyles>;

export type ButtonVariant = NonNullable<ButtonStyleProps["variant"]>;
export type ButtonTone = NonNullable<ButtonStyleProps["tone"]>;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  iconOnly?: boolean;
  leadingIcon?: ReactNode;
  loading?: boolean;
  // Forwarded onto the underlying <button> through the props spread (React 19
  // passes ref as a plain prop), so a caller can move focus to the control.
  ref?: Ref<HTMLButtonElement>;
  /** Visual selection for a neutral secondary toggle; pair with the appropriate ARIA state. */
  selected?: boolean;
  size?: ButtonSize;
  tone?: ButtonTone;
  trailingIcon?: ReactNode;
  variant?: ButtonVariant;
}

const sizes: Record<ButtonSize, string> = {
  sm: "h-[var(--control-h-sm)] px-[var(--button-pad-x-sm)] text-[length:var(--type-eyebrow)] tracking-[var(--type-tracking-chip)] uppercase",
  md: "text-[length:var(--type-label)] h-[var(--control-h-md)] px-[var(--button-pad-x-md)] tracking-[var(--type-tracking-chip)] uppercase",
  lg: "text-[length:var(--type-label)] h-[var(--control-h-lg)] px-[var(--button-pad-x-lg)] tracking-[var(--type-tracking-chip)] uppercase",
};

const iconOnlySizes: Record<ButtonSize, string> = {
  sm: "size-[var(--icon-button-sm)]",
  md: "size-[var(--icon-button-md)]",
  lg: "size-[var(--icon-button-lg)]",
};

export function Button({
  children,
  className,
  disabled,
  iconOnly,
  leadingIcon,
  loading = false,
  selected = false,
  size = "md",
  style,
  tone = "neutral",
  trailingIcon,
  type = "button",
  variant = "secondary",
  ...props
}: ButtonProps) {
  return (
    <button
      aria-busy={loading ? "true" : "false"}
      className={cn(
        buttonStyles({ selected, tone, variant }),
        !disabled && !loading && "transition-[background-color,border-color,color] duration-200",
        loading && "cursor-default",
        disabled && !loading && "cursor-not-allowed",
        iconOnly
          ? cn(iconOnlySizes[size], "rounded-md px-0 normal-case tracking-normal")
          : sizes[size],
        className,
      )}
      disabled={disabled || loading}
      style={{ ...style, fontWeight: "var(--type-weight-extrabold)" }}
      type={type}
      {...props}
    >
      {loading ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
}

interface ButtonLinkProps {
  children: ReactNode;
  onNavigated?(): void;
  href: string;
  className?: string;
  size?: ButtonSize;
  tone?: ButtonTone;
  variant?: ButtonVariant;
  "aria-label"?: string;
}

/**
 * Navigation that looks like a button. It shares `Button`'s styling so the two
 * cannot drift, and renders an anchor so modified clicks, middle-click, and
 * "open in new tab" behave natively — none of which a `<button>` can offer.
 * Reach for this whenever the control's job is to go somewhere.
 */
export function ButtonLink({
  children,
  className,
  href,
  size = "md",
  tone = "neutral",
  variant = "secondary",
  ...props
}: ButtonLinkProps) {
  return (
    <RouterLink
      className={cn(
        buttonStyles({ tone, variant }),
        "transition-[background-color,border-color,color] duration-200",
        sizes[size],
        "[font-weight:var(--type-weight-extrabold)]",
        className,
      )}
      href={href}
      {...props}
    >
      {children}
    </RouterLink>
  );
}
